import { type TezosToolkit } from "@taquito/taquito";
import {
  type ChainFullId,
  ChainNamespaces,
  type SignTransactionOptions,
  executeOperations,
  parseChainFullId,
  totalFee,
  useChainRegistry,
} from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import {
  WcScenarioType,
  useAsyncActionHandler,
  useValidateWcRequest,
  walletKit,
} from "@umami/state";
import { CustomError, WalletConnectError, WcErrorCode, getErrorContext } from "@umami/utils";
import { type JsonRpcResponse, formatJsonRpcResult } from "@walletconnect/jsonrpc-utils";
import { useForm } from "react-hook-form";

import { SuccessStep } from "../SuccessStep";
import { type CalculatedSignProps, type SdkSignPageProps } from "../utils";

import {
  isEthSignTransaction,
  isEthSignTypedData,
  isEthTransactionOperation,
  isPersonalSign,
} from "@/components/WalletConnect/typeGuards";

export const useSignWithWalletConnect = ({
  operation,
  headerProps,
  chainId,
}: SdkSignPageProps & { chainId: ChainFullId }): CalculatedSignProps => {
  const { isLoading: isSigning, handleAsyncAction } = useAsyncActionHandler();
  const { openWith, goBack } = useDynamicModalContext();
  const validateWcRequest = useValidateWcRequest();
  const { registry } = useChainRegistry();

  const form = useForm({ defaultValues: { executeParams: operation.estimates } });
  const requestId = headerProps.requestId;

  if (requestId.sdkType !== "walletconnect") {
    return {
      fee: 0,
      isSigning: false,
      onSign: async () => {},
      network: null,
    };
  }

  const onSign = async (tezosToolkit: TezosToolkit, options: SignTransactionOptions) =>
    handleAsyncAction(
      async () => {
        validateWcRequest("request", requestId.id, WcScenarioType.APPROVE, goBack);

        if (headerProps.requestId.sdkType !== "walletconnect") {
          throw new WalletConnectError("Invalid request type", WcErrorCode.UNKNOWN_ERROR, null);
        }
        let opHash = "";
        let response: JsonRpcResponse;
        try {
          const { namespace } = parseChainFullId(chainId);
          switch (namespace) {
            case ChainNamespaces.TEZOS:
              {
                const result = await executeOperations(
                  { ...operation, estimates: form.watch("executeParams") },
                  tezosToolkit
                );
                opHash = result.opHash;

                response = formatJsonRpcResult(requestId.id, {
                  hash: opHash,
                  operationHash: opHash,
                });
              }
              break;
            case ChainNamespaces.EVM:
              {
                const firstOperation = operation.operations[0];

                if (isPersonalSign(firstOperation)) {
                  opHash = await registry.signMessage(chainId, firstOperation.message, options);
                } else if (isEthSignTransaction(firstOperation)) {
                  const transactionToSend = {
                    to:
                      firstOperation.transaction.to || "0x0000000000000000000000000000000000000000",
                    value: firstOperation.transaction.value || "0",
                    data: firstOperation.transaction.data || "",
                  };

                  const signedTx = await registry.signTransaction(
                    chainId,
                    transactionToSend,
                    options
                  );
                  opHash = signedTx.hash || "transaction_signed";
                } else if (isEthSignTypedData(firstOperation)) {
                  delete firstOperation.typedData.types.EIP712Domain;
                  const signature = await registry.signTypedData(
                    chainId,
                    firstOperation.typedData,
                    options
                  );
                  response = formatJsonRpcResult(requestId.id, signature);
                  await walletKit.respondSessionRequest({ topic: requestId.topic, response });
                  // Set opHash to indicate success and continue to success step
                  opHash = "typed_data_signed";
                } else if (isEthTransactionOperation(firstOperation)) {
                  const transactionToSend = {
                    to: firstOperation.to,
                    contract: firstOperation.to,
                    value: firstOperation.value,
                    data: firstOperation.data,
                  };

                  const signedTx = await registry.signAndSendTransaction(
                    chainId,
                    transactionToSend,
                    options
                  );

                  if (!signedTx.hash) {
                    throw new Error("Transaction hash is missing from signed transaction");
                  }

                  opHash = signedTx.hash;
                } else {
                  throw new Error(
                    `Unsupported operation type: ${(firstOperation as { type?: string }).type}`
                  );
                }
              }
              break;
            case ChainNamespaces.BITCOIN:
              throw new CustomError("Bitcoin is not supported yet");
          }

          response = {
            id: headerProps.requestId.id,
            result: opHash,
            jsonrpc: "2.0",
          };
          await walletKit.respondSessionRequest({ topic: requestId.topic, response });
        } catch (error: unknown) {
          const errorContext = getErrorContext(error);

          // For typed data signing, show error without hash
          if (opHash === "typed_data_signed") {
            await openWith(
              <SuccessStep dAppNotificationError={errorContext.description} hash="" />,
              { canBeOverridden: true }
            );
          } else {
            await openWith(
              <SuccessStep dAppNotificationError={errorContext.description} hash={opHash} />,
              { canBeOverridden: true }
            );
          }

          // Type guard to check if error has processed property
          if (error && typeof error === "object" && "processed" in error) {
            (error as { processed: boolean }).processed = true; // no toast for this error
          }
          throw error;
        }

        // For typed data signing, show success without hash
        if (opHash === "typed_data_signed") {
          return openWith(<SuccessStep hash="" />, { canBeOverridden: true });
        }

        return openWith(<SuccessStep hash={opHash} />, { canBeOverridden: true });
      },
      (error: { message: string }) => ({
        description: `Failed to perform WalletConnect operation: ${error.message}`,
      })
    );

  return {
    fee: totalFee(form.watch("executeParams")),
    isSigning,
    onSign,
    network: headerProps.network,
  };
};
